import { SessionData } from '@marsinet/commons';

// Usa la lista IANA del navegador cuando existe; en navegadores antiguos emplea esta alternativa.
const fallbackTimeZones = [
  'Africa/Cairo',
  'Africa/Casablanca',
  'Africa/Johannesburg',
  'America/Anchorage',
  'America/Buenos_Aires',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/New_York',
  'America/Sao_Paulo',
  'Asia/Bangkok',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Atlantic/Azores',
  'Australia/Sydney',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Moscow',
  'Pacific/Auckland',
  'Pacific/Honolulu',
];

type IntlWithTimeZoneEnumeration = typeof Intl & {
  supportedValuesOf?: (key: 'timeZone') => string[];
};

const intlWithTimeZoneEnumeration = Intl as IntlWithTimeZoneEnumeration;
const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const availableTimeZones =
  intlWithTimeZoneEnumeration.supportedValuesOf?.('timeZone') ??
  fallbackTimeZones;

// Incluye UTC y la zona del navegador aunque no aparezcan en la lista.
export const timezones = Array.from(
  new Set([...availableTimeZones, 'UTC', browserTimeZone])
)
  .sort()
  .map((timeZone) => ({ label: timeZone, value: timeZone }));

export const getSexOptions = (t) => [
  { label: t('pages.patients.form.sexOptionMale'), value: 'M' },
  { label: t('pages.patients.form.sexOptionFemale'), value: 'F' },
];

export const defaultSessionData: SessionData = {
  total_steps_automatic_forward: 0,
  total_steps_automatic_backward: 0,
  total_steps_intention_forward: 0,
  total_steps_intention_backward: 0,

  // NUEVAS para steps ASN1 y ASN2
  total_steps_aan1_forward: 0,
  total_steps_aan1_backward: 0,
  total_steps_aan2_forward: 0,
  total_steps_aan2_backward: 0,

  total_steps_automatic: 0,
  total_steps_intention: 0,
  total_steps_aan1: 0,
  total_steps_aan2: 0,
  total_steps_forward: 0,
  total_steps_backward: 0,

  total_time_automatic_forward: 0,
  total_time_automatic_backward: 0,
  total_time_intention_forward: 0,
  total_time_intention_backward: 0,

  // NUEVAS para tiempo ASN1 y ASN2
  total_time_aan1_forward: 0,
  total_time_aan1_backward: 0,
  total_time_aan2_forward: 0,
  total_time_aan2_backward: 0,

  total_time_automatic: 0,
  total_time_intention: 0,
  total_time_aan1: 0,
  total_time_aan2: 0,
  total_time_forward: 0,
  total_time_backward: 0,

  total_steps_total: 0,
  total_time_total: 0,
  total_time_use: 0,
  total_time_walking: 0,
  total_time_standing: 0,

  median_total_steps: 0,
  median_total_steps_automatic_forward: 0,
  median_total_steps_automatic_backward: 0,
  median_total_steps_intention_forward: 0,
  median_total_steps_intention_backward: 0,
  median_total_steps_automatic: 0,
  median_total_steps_intention: 0,
  median_total_steps_forward: 0,
  median_total_steps_backward: 0,

  median_total_time: 0,
  median_total_time_automatic_forward: 0,
  median_total_time_automatic_backward: 0,
  median_total_time_intention_forward: 0,
  median_total_time_intention_backward: 0,

  median_total_time_automatic: 0,
  median_total_time_intention: 0,

  median_total_time_forward: 0,
  median_total_time_backward: 0,

  evaluation: 0,
  cadence_automatic_backward: 0,
  cadence_automatic_forward: 0,
  cadence_intention_backward: 0,
  cadence_intention_forward: 0,
  flexos_hip: 0,
  flexos_knee: 0,
  flexos_ankle: 0,
  threshold_hipl: 0,
  threshold_hipr: 0,
  threshold_kneel: 0,
  threshold_kneer: 0,
  effort: 0,
  torque_hipl: 0,
  torque_hipr: 0,
  torque_kneel: 0,
  torque_kneer: 0,
  rom_hipl: 0,
  rom_hipr: 0,
  rom_kneel: 0,
  rom_kneer: 0,
  fc_max: 0,
  fc_med: 0,
  so2: 0,
};

export const getPathologyOptions = (t) => [
  {
    label: t('pages.patients.form.pathologies.spastic'),
    value: 'spastic',
  },
  {
    label: t('pages.patients.form.pathologies.dyskinetic'),
    value: 'dyskinetic',
  },
  {
    label: t('pages.patients.form.pathologies.ataxic'),
    value: 'ataxic',
  },
  {
    label: t('pages.patients.form.pathologies.mixed'),
    value: 'mixed',
  },
  { label: t('pages.patients.form.pathologies.ame1'), value: 'ame1' },
  { label: t('pages.patients.form.pathologies.ame2'), value: 'ame2' },
  { label: t('pages.patients.form.pathologies.ame3'), value: 'ame3' },
  {
    label: t('pages.patients.form.pathologies.duchenne'),
    value: 'duchenne',
  },
  {
    label: t('pages.patients.form.pathologies.muscularDystrophy'),
    value: 'muscularDystrophy',
  },
  {
    label: t('pages.patients.form.pathologies.myopathy'),
    value: 'myopathy',
  },
  {
    label: t('pages.patients.form.pathologies.myasthenia'),
    value: 'myasthenia',
  },
  {
    label: t('pages.patients.form.pathologies.cervical'),
    value: 'spinalCordInjury',
  },
  {
    label: t('pages.patients.form.pathologies.cervical'),
    value: 'cervical',
  },
  {
    label: t('pages.patients.form.pathologies.thoracic'),
    value: 'thoracic',
  },
  {
    label: t('pages.patients.form.pathologies.lumbar'),
    value: 'lumbar',
  },
  {
    label: t('pages.patients.form.pathologies.acquiredBrainInjury'),
    value: 'acquiredBrainInjury',
  },
  {
    label: t('pages.patients.form.pathologies.rett'),
    value: 'rettSyndrome',
  },
  {
    label: t('pages.patients.form.pathologies.wesSyndrome'),
    value: 'wesSyndrome',
  },
  {
    label: t('pages.patients.form.pathologies.other'),
    value: 'other',
  },
];

export const getAffectionOptions = (t) => [
  {
    label: t('pages.patients.form.mobilityLevels.level1NoRestrictions'),
    value: 'level1NoRestrictions',
  },
  {
    label: t('pages.patients.form.mobilityLevels.level2WithLimitations'),
    value: 'level2WithLimitations',
  },
  {
    label: t('pages.patients.form.mobilityLevels.level3WithAssistiveDevice'),
    value: 'level3WithAssistiveDevice',
  },
  {
    label: t('pages.patients.form.mobilityLevels.level4LimitedSelfMobility'),
    value: 'level4LimitedSelfMobility',
  },
  {
    label: t('pages.patients.form.mobilityLevels.level5WheelchairTransport'),
    value: 'level5WheelchairTransport',
  },
];

// Graphs constants labels

// Security status states
export const securityStatusLabels = {
  0: 'Start',
  1: 'Error',
  2: 'Warning',
  3: 'OK',
};

// Global position states
export const globalPositionLabels = {
  0: 'None',
  1: 'Stand',
  2: 'Sit',
  3: 'Between Sit and Stand',
  4: 'Walking',
  5: 'Wheelchair',
};

// Atlas states
export const stateLabelsAtlas = {
  0: 'Stopped',
  1: 'Walking',
  2: 'Sitting',
  3: 'Sit',
  4: 'Standing',
  5: 'Stand',
  6: 'Begin walk',
  7: 'End walk',
};

// Explorer states
export const stateLabelsExplorer = {
  0: 'Disabled',
  1: 'Walking',
  2: 'Sitting',
  3: 'Initializing',
  4: 'Standing',
  5: 'Enabled',
  6: 'Begin walk',
  7: 'End walk',
  8: 'Triggering by force',
  9: 'Elevation down',
  10: 'Elevation up',
};

// Explorer clinic states
export const stateLabelsExplorerClinic = {
  ...stateLabelsExplorer,
  11: 'Walking AAN',
  12: 'Zero force',
  13: 'Walking AAN-ZF',
};
